import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { clsx as cx } from 'clsx';
import Icon from '../icon';
import { MenuLabel } from '../main-menu/main-menu.css';

export const THEMES = [
	{
		id: 'midnight',
		label: 'Noir',
		swatch: '#265bad'
	},
	{
		id: 'aurora',
		label: 'Jade Reel',
		swatch: '#18a080'
	},
	{
		id: 'ember',
		label: 'Technicolor',
		swatch: '#e07012'
	},
	{
		id: 'neon',
		label: 'Ultraviolet',
		swatch: '#8b2cf5'
	},
	{
		id: 'solstice',
		label: 'Cyan Scope',
		swatch: '#0fc7e6'
	},
	{
		id: 'verdigris',
		label: 'Patina',
		swatch: '#26a18f'
	},
	{
		id: 'sepia',
		label: 'Sepia Print',
		swatch: '#b8893c'
	},
	{
		id: 'ruby',
		label: 'Red Carpet',
		swatch: '#c43a4f'
	},
	{
		id: 'liquid-glass',
		label: 'Glass Plate',
		swatch: '#7dd9ff'
	},
	{
		id: 'rainbow',
		label: 'Prism Reel',
		swatch: 'linear-gradient(90deg, #ff5f6d 0%, #ffc371 20%, #ffe66d 40%, #4ade80 60%, #60a5fa 80%, #c084fc 100%)'
	},
	{
		id: 'contrast',
		label: 'Silver Screen',
		swatch: '#ffffff'
	}
];

export const STORAGE_KEY = 'px-theme';

export function getInitialTheme() {
	try {
		return localStorage.getItem(STORAGE_KEY) || 'midnight';
	}
	catch{
		return 'midnight';
	}
}

export function applyTheme(id) {
	document.documentElement.setAttribute('data-theme', id);

	try {
		localStorage.setItem(STORAGE_KEY, id);
	}
	catch{
		// ignore
	}
}

function ThemeSwitcher({ isMaximized, onMenuItemClick }) {
	const [activeTheme, setActiveTheme] = useState(getInitialTheme);
	const [isOpen, setIsOpen] = useState(false);
	const containerRef = useRef(null);

	// Apply persisted theme on mount
	useEffect(() => {
		applyTheme(activeTheme);
	}, []);

	// Close popout on outside click
	useEffect(() => {
		if( !isOpen ) {
			return;
		}

		const handleOutside = (e) => {
			if( containerRef.current && !containerRef.current.contains(e.target) ) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleOutside);
		return () => {
			document.removeEventListener('mousedown', handleOutside);
		};
	}, [isOpen]);

	const handleSelect = useCallback((id) => {
		setActiveTheme(id);
		applyTheme(id);
		setIsOpen(false);
	}, []);

	return (
		<div
			ref={containerRef}
			className="relative"
			data-tooltip={isMaximized ? undefined : 'Theme'}
			data-tooltip-position="right"
		>
			{/* Trigger — same nav-row pattern as links */}
			<button
				type="button"
				className={cx(
					'theme-switcher-btn nav-row',
					'flex items-center flex-nowrap w-full text-left',
					'border-0 border-l-2 border-solid bg-transparent p-0',
					'text-sm font-bold focus:outline-none cursor-pointer',
					'hover:bg-nav-hover hover:text-white transition-colors duration-fast',
					isOpen
						? 'border-primary text-white'
						: 'border-transparent text-white text-opacity-30'
				)}
				onClick={() => {
					onMenuItemClick && onMenuItemClick();
					setIsOpen((v) => !v);
				}}
				aria-label="Switch theme"
				data-cy="theme-switcher"
				data-testid="theme-switcher"
			>
				<Icon
					use="theme"
					size={1.25}
					className={cx('theme-switcher__icon', isOpen ? 'opacity-100' : 'opacity-30')}
					style={{ margin: '1rem 1.375rem' }}
				/>
				<MenuLabel className={isMaximized ? 'block' : 'hidden'}>
					Theme
				</MenuLabel>
			</button>

			{isOpen && (
				<div
					className="absolute bottom-full left-0 z-dropdown mb-2 rounded-lg bg-panel p-2 lg:bottom-0 lg:left-full lg:mb-0"
					style={{ minWidth: '11rem', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}
				>
					<p className="m-0 mb-1 px-2 py-1 text-white text-opacity-40 text-xs font-bold tracking-widest uppercase">
						Theme
					</p>
					{THEMES.map((theme) => {
						const isActive = theme.id === activeTheme;

						return (
							<button
								key={theme.id}
								type="button"
								className={cx(
									'theme-option flex items-center w-full text-left',
									'border-0 rounded-md px-2 py-2 cursor-pointer',
									'text-sm transition-colors duration-fast focus:outline-none',
									isActive
										? 'theme-option--active font-bold text-white'
										: 'font-normal text-white text-opacity-60',
									'hover:text-white'
								)}
								style={{
									gap: '0.625rem',
									background: isActive ? 'rgba(38,91,173,0.2)' : 'transparent'
								}}
								onClick={() => handleSelect(theme.id)}
							>
								<span
									className="flex-shrink-0 rounded-full pointer-events-none"
									style={{
										width: '0.75rem',
										height: '0.75rem',
										background: theme.swatch,
										boxShadow: '0 0 0 1px rgba(255,255,255,0.15)'
									}}
								/>
								{theme.label}
							</button>
						);
					})}
				</div>
			)}
		</div>
	);
}

ThemeSwitcher.propTypes = {
	isMaximized: PropTypes.bool.isRequired,
	onMenuItemClick: PropTypes.func
};

export default ThemeSwitcher;
